import React from 'react';

import HeaderText from '../../Common/HeaderText';
import { ReactComponent as GoogleIcon } from '@repo/common/assets/icons/16px/icon_16px_google.svg';
import { saveAuthReturnTo, useAuth, useIsMobile, useT } from '@repo/common';
import { startAppleConnect } from '../../../../api/user/user.api';

/** 구글 아이콘은 svg 파일로 들어와 있지만 애플은 에셋이 없어 인라인으로 둔다. */
const AppleIcon = () => (
  <svg
    className='size-[31px]'
    viewBox='0 0 24 24'
    fill='currentColor'
    aria-hidden='true'
  >
    <path d='M16.365 1.43c0 1.14-.42 2.2-1.12 3.02-.84.99-2.2 1.76-3.34 1.67-.14-1.1.44-2.26 1.1-3.01.75-.86 2.06-1.5 3.19-1.56.06.09.17.62.17.88zM20.6 17.3c-.55 1.27-.82 1.84-1.53 2.96-.99 1.57-2.38 3.52-4.1 3.53-1.53.02-1.93-1-4.01-.99-2.08.01-2.51 1.01-4.05.99-1.72-.02-3.04-1.78-4.03-3.34C.1 16.09-.2 10.9 1.5 8.13c1.2-1.96 3.1-3.11 4.88-3.11 1.82 0 2.96 1 4.47 1 1.46 0 2.35-1 4.45-1 1.59 0 3.27.87 4.47 2.36-3.93 2.16-3.29 7.78 1.83 9.92z' />
  </svg>
);

/**
 * 소셜 로그인 연동 관리.
 *
 * <p>연동 상태는 user.socialAccounts 를 기준으로 본다. 예전에는 socialEmail 하나만 보고
 * "Google 연결됨" 으로 단정해서, 애플로 가입한 사람에게도 구글이라고 표시됐다.
 */
const SocialLoginForm = ({ onDisconnectOpen }) => {
  const t = useT();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const accounts = user?.socialAccounts ?? [];
  const email = user?.email;
  const hasPw = user?.hasPw;

  const linkedOf = (provider) =>
    accounts.find((account) => account.provider === provider) ?? null;

  const connectGoogle = () => {
    const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const GOOGLE_REDIRECT_URI = process.env.REACT_APP_GOOGLE_REDIRECT_URI;
    saveAuthReturnTo('/mypage');
    localStorage.setItem('authReturnMenu', 'Account setting');

    window.location.href =
      'https://accounts.google.com/o/oauth2/v2/auth' +
      `?client_id=${GOOGLE_CLIENT_ID}` +
      `&redirect_uri=${GOOGLE_REDIRECT_URI}` +
      `&response_type=code` +
      `&scope=${encodeURIComponent('email profile')}` +
      `&prompt=select_account` +
      `&state=connect`;
  };

  const connectApple = () => {
    saveAuthReturnTo('/mypage');
    localStorage.setItem('authReturnMenu', 'Account setting');
    startAppleConnect('/mypage');
  };

  /**
   * 해제해도 계정에 다시 들어올 수 있는지 따진다.
   * 비밀번호 로그인이 있거나, 다른 소셜이 하나라도 남아 있으면 된다.
   */
  const requestDisconnect = (provider) => {
    const hasOtherSocial = accounts.some(
      (account) => account.provider !== provider,
    );
    if (hasOtherSocial) {
      onDisconnectOpen({ provider, reason: 'can-disconnect' });
      return;
    }

    if (!email && !hasPw) {
      onDisconnectOpen({ provider, reason: 'no-email-no-pw' });
    } else if (email && !hasPw) {
      onDisconnectOpen({ provider, reason: 'email-no-pw' });
    } else if (!email && hasPw) {
      onDisconnectOpen({ provider, reason: 'no-email-has-pw' });
    } else {
      onDisconnectOpen({ provider, reason: 'can-disconnect' });
    }
  };

  const providers = [
    {
      key: 'GOOGLE',
      label: 'Google',
      icon: <GoogleIcon className='size-[31px]' />,
      onConnect: connectGoogle,
      connectedText: t(
        'Connected — You can sign in using your Google account.',
        '연결됨 — Google 계정으로 로그인할 수 있습니다.',
      ),
    },
    {
      key: 'APPLE',
      label: 'Apple',
      icon: <AppleIcon />,
      onConnect: connectApple,
      connectedText: t(
        'Connected — You can sign in using your Apple account.',
        '연결됨 — Apple 계정으로 로그인할 수 있습니다.',
      ),
    },
  ];

  const renderRow = ({ key, label, icon, onConnect, connectedText }) => {
    const linked = linkedOf(key);
    const onClick = linked ? () => requestDisconnect(key) : onConnect;
    const actionLabel = linked
      ? t('Disconnect', '연결 해제')
      : t('Connect', '연결하기');
    const statusText = linked
      ? connectedText
      : t('Not connected', '연결되지 않음');

    return (
      <div
        key={key}
        className='flex tabletH:px-2 tabletH:py-4 items-center justify-between rounded-lg bg-transparent tabletH:bg-grayscale-gray00 w-full'
      >
        {isMobile ? (
          <div className='flex flex-col w-full'>
            <div className='flex items-center justify-between w-full h-6'>
              <div className='typo-b14Strong text-text-primary'>{label}</div>
              <button
                className='typo-b12Strong text-text-primary underline'
                onClick={onClick}
              >
                {actionLabel}
              </button>
            </div>
            <div className='typo-b13 text-text-placeholder'>{statusText}</div>
          </div>
        ) : (
          <>
            <div className='flex w-full'>
              <div className='px-2'>
                <div className='p-[3px]'>{icon}</div>
              </div>
              <div className='flex flex-col px-2 items-start w-full'>
                <div className='typo-b14Strong text-text-primary'>{label}</div>
                <div className='typo-b12 text-text-placeholder'>
                  {statusText}
                </div>
              </div>
            </div>
            <div className='flex items-center px-2'>
              <button
                className='typo-b14 text-text-primary underline'
                onClick={onClick}
              >
                {actionLabel}
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className='flex flex-col max-w-[568px] w-full items-start pb-[60px]'>
      <HeaderText label={t('Social Login', '소셜 로그인')} />
      {/* 모바일은 행에 세로 패딩이 없어 간격을 더 준다 */}
      <div className='flex flex-col w-full gap-5 tabletH:gap-2'>
        {providers.map(renderRow)}
      </div>
    </div>
  );
};

export default SocialLoginForm;
